import { useNavigate } from '@tanstack/react-location';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import useAccessCodeApi from '../../api/use-access-code-api';
import CtaButton from '../../components/cta-button';
import Header from '../../components/header';
import AuthModal from '../../components/modal/auth-modal';
import { useModalToggle } from '../../components/modal/portal';
import WaitingListModal from '../../components/modal/waiting-list-modal';
import SiteFooter from '../../components/site-footer';
import { useFlexAssessmentContext } from '../../context/context';
import buttons from '../../css/buttons.module.scss';
import styles from '../../css/landing.module.scss';
import Hero from '../../media/hero.webp';
import { ReactComponent as IconLock } from '../../media/icon-lock.svg';
import SideBySideCoaching from '../../media/img-coaching.webp';
import SideBySideCoreloop from '../../media/img-coreloop.webp';
import SideBySideFlexScore from '../../media/img-flex-score.webp';
import SideBySideTracker from '../../media/img-tracker.webp';
import { ReactComponent as StepDownload } from '../../media/step-download.svg';
import { ReactComponent as StepJourney } from '../../media/step-journey.svg';
import { ReactComponent as StepRegister } from '../../media/step-register.svg';
import { FlexAssessmentContextAction } from '../../types/flex-assessment-context-action';
import { NotificationType } from '../../types/notification-type';
import { PAGE_URLS } from '../../utils/constants';
import { MIXPANEL_EVENTS } from '../../utils/mixpanel-consts';
import { track } from '../../utils/mixpanel-helper';
import { STRINGS } from '../../utils/strings';
import { usePageScrolledWithTopBuffer } from '../../utils/use-page-scrolled';
import { useScrollToTop } from '../../utils/use-scroll-to-top';

const SIDE_BY_SIDE_CONTENT: {
  title: string;
  description: string;
  buttonLabel: string;
  image: string;
}[] = [
  {
    title: 'Expert on-demand support, just for you',
    description:
      'Confidential access to a board-certified health coach to help you manage stress and reduce the risk of burnout, anytime you need it.',
    image: SideBySideCoaching,
    buttonLabel: 'Connect with a Coach',
  },
  {
    title: 'React to the highs and lows of life, anytime, and privately',
    description:
      'Engage in the personalized and private app from your phone anywhere, and at any time. Rest assured knowing all information shared in-app is confidential and not shared with your employer.',
    image: SideBySideTracker,
    buttonLabel: 'Join Today',
  },
  {
    title: 'Engaging, enlightening, and fun',
    description:
      'Pivot Flex’s practical tools and challenges make changing the way you react to stress more accessible and interesting than ever before. What you learn is easy-to-use in real life situations.',
    image: SideBySideCoreloop,
    buttonLabel: 'Get Started Today',
  },
  {
    title: 'Track your progress as you build resilience',
    description:
      "Watch your resilience build in real-time with the help of progress tracking. Measure your success and be proud of how far you've come.",
    image: SideBySideFlexScore,
    buttonLabel: 'Try Something New',
  },
];

const ONBOARDING_STEPS: {
  buttonLabel: string;
  image: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  {
    image: StepRegister,
    buttonLabel: 'Register',
  },
  {
    image: StepDownload,
    buttonLabel: 'Download',
  },
  {
    image: StepJourney,
    buttonLabel: 'Flex Journey',
  },
];

export default function Landing(): JSX.Element {
  useScrollToTop();
  const navigate = useNavigate();
  const { state, dispatch } = useFlexAssessmentContext();
  const { isOn: isCheckEmailModalOpen, toggle: toggleCheckEmailModal } =
    useModalToggle();
  const { isOn: isWaitListModalOpen, toggle: toggleWaitListModal } =
    useModalToggle();
  const { checkIsAccessCodeAllowed, checkIsAccessCodeIsFull } =
    useAccessCodeApi();
  const eligibilityCopy = state?.orgConfig?.descriptionCopy;
  const isAccessCodeDisabled: boolean = !checkIsAccessCodeAllowed(
    state.accessCodeConfig
  );
  const isAccessCodeFull: boolean = checkIsAccessCodeIsFull(
    state.accessCodeConfig
  );
  const ctaButtonRef = useRef<HTMLDivElement>(null);
  const isInBuffer = usePageScrolledWithTopBuffer(ctaButtonRef);

  useEffect(() => {
    if (isAccessCodeFull && !isWaitListModalOpen) {
      toggleWaitListModal();
    }
  }, [isAccessCodeFull]);

  useEffect(() => {
    if (isAccessCodeDisabled) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          type: NotificationType.Error,
          isShowing: true,
          message: `${STRINGS.ERRORS.ACCESS_CODE_NOT_AVAILABLE}. Error: ${STRINGS.ERRORS.ACCESS_CODE_ERROR}`,
        },
      });
    }
  }, [dispatch, isAccessCodeDisabled]);

  const onCta = () => {
    if (isAccessCodeFull) {
      navigate({ to: PAGE_URLS.WAITING_LIST });
    } else {
      toggleCheckEmailModal();
    }
  };

  return (
    <>
      <Header />
      <section className={styles.hero}>
        <div className={styles.heroWrapper}>
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>
              Pivot Flex is the platform you need to manage stress and prevent
              burnout
            </h1>
            <p className={styles.heroCopy}>
              Start with our signature Flex Assessment. Adapted from four
              clinically validated surveys, it gives you instant, precise
              feedback. And, if you want, get a deep-dive convo with one of our
              top-tier certified coaches.
            </p>
            <p className={styles.heroCopy}>
              We’re excited to be there for you.
            </p>
            <div
              className={clsx(styles.ctaDock, {
                [styles.isDocked]: !isAccessCodeDisabled && isInBuffer,
              })}
              ref={ctaButtonRef}
            >
              <button
                disabled={isAccessCodeDisabled}
                className={clsx(
                  styles.cta,
                  buttons.buttonOutlined,
                  styles.heroCta
                )}
                onClick={() => {
                  onCta();
                  track(MIXPANEL_EVENTS.BUTTON_CLICK, {
                    button: 'init-signup',
                    position: 'hero',
                  });
                }}
                data-testid="mainCta"
              >
                Join the Pilot!
              </button>
            </div>
          </div>
          <div className={styles.heroRight}>
            <img src={Hero} alt="" />
          </div>
        </div>
      </section>
      <section className={styles.sideBySides}>
        {SIDE_BY_SIDE_CONTENT.map((item, i) => {
          const { title, description, image, buttonLabel } = item;
          return (
            <div key={title} className={styles.sideBySide}>
              <div className={styles.sideBySideCopy}>
                <h2>{title}</h2>
                <p className={styles.p}>{description}</p>
                {/* // Disabled until launch
                i === 0 && eligibilityCopy && (
                  <>
                    <hr className={styles.hr} />
                    <small className={styles.finePrint}>
                      * {eligibilityCopy}
                    </small>
                  </>
                )*/}
                <CtaButton
                  position="side-by-side"
                  label={buttonLabel}
                  isDisabled={isAccessCodeDisabled}
                  toggleCheckEmailModal={toggleCheckEmailModal}
                />
              </div>
              <div className={styles.sideBySideImg}>
                <img src={image} alt="" />
              </div>
            </div>
          );
        })}
      </section>
      <section className={styles.focusAreas}>
        <h3 className={styles.focusAreasTitle}>Easy as 1, 2, 3!</h3>
        <div
          className={styles.focusAreasWrapper}
          style={{
            gridTemplateColumns: `repeat(${ONBOARDING_STEPS.length}, minmax(0, 1fr))`,
          }}
        >
          {ONBOARDING_STEPS.map((item) => {
            const { buttonLabel, image: FocusAreaIcon } = item;
            return (
              <div key={buttonLabel} className={styles.focusArea}>
                <FocusAreaIcon />
                {buttonLabel}
              </div>
            );
          })}
        </div>
      </section>
      <div className={styles.legalNotice}>
        <div className={styles.legalNoticeWrapper}>
          <IconLock />
          <p>
            Pivot Flex does not replace clinical care. The contents of Flex —
            including text, images, graphics, and exercises — cannot replace
            clinical services provided by a healthcare provider. Additionally,
            Flex does not provide emergency services or immediate assistance in
            crisis situations
          </p>
        </div>
      </div>
      <SiteFooter />
      <AuthModal
        isOpen={isCheckEmailModalOpen}
        onClose={() => {
          onCta();
          track(MIXPANEL_EVENTS.BUTTON_CLICK, {
            button: 'init-signup',
            position: 'auth-modal',
          });
        }}
      />
      <WaitingListModal isOpen={isWaitListModalOpen} />
    </>
  );
}
